import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Lightbulb, Code, Rocket } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Understand the Business",
    description:
      "We start by learning what is slowing your growth online and what needs to improve.",
  },
  {
    number: "02",
    icon: Lightbulb,
    title: "Define the Right Solution",
    description:
      "We map out the service, structure, and priorities before anything gets built.",
  },
  {
    number: "03",
    icon: Code,
    title: "Build With Purpose",
    description:
      "Everything is created for usability, visibility, and long-term performance.",
  },
  {
    number: "04",
    icon: Rocket,
    title: "Launch and Support",
    description:
      "We stay involved with updates, improvements, and ongoing support after launch.",
  },
];

export const ProcessSection = () => {
  return (
    <section className="pt-8 sm:pt-10 lg:pt-0 pb-12 sm:pb-14 lg:pb-16 bg-muted/30 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl opacity-60" />
        <div className="absolute -bottom-24 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-14 lg:mb-20"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            How We Work
          </span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mt-3 mb-4 sm:mb-6">
            A Clear Process From Strategy to{" "}
            <span className="gradient-text">Launch</span>
          </h2>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-7 lg:gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                className="relative"
              >
                {/* Connector line (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-14 left-full w-full h-0.5 bg-gradient-to-r from-primary/40 to-transparent -z-10" />
                )}

                <div className="h-full rounded-2xl bg-card border border-border p-5 sm:p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  {/* Icon + number */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="relative inline-block mb-4 sm:mb-6"
                  >
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full gradient-primary-soft flex items-center justify-center mx-auto">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background/80 border border-border flex items-center justify-center">
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
                      </div>
                    </div>
                    <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground shadow-glow">
                      {step.number}
                    </div>
                  </motion.div>

                  {/* Text */}
                  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2 sm:mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};